"use client";
import type React from "react";
import { motion } from "framer-motion";
import { DollarSign, Package, Users } from "lucide-react";
import { CurrencySummary, Summary } from "../../pages/SalesPage/SalesPageInterface";
import { formatCurrency, getCurrencySymbol } from "../../utils/formatters";
import { useAuth } from "../../context/AuthContext";
import { dictionary } from "../../utils/dictionary";

interface CardProps {
  title: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  compact?: boolean;
}

const StatCard = ({ title, value, icon, compact = false }: CardProps) => {
  return (
    <motion.div
      layout
      transition={{ duration: 0.2, ease: "easeOut" }}
      className={
        compact
          ? "min-w-[150px] rounded-xl border border-gray-100 bg-white px-3.5 py-2 shadow-sm"
          : "bg-white p-4 rounded-xl shadow-sm"
      }
    >
      <div className="flex items-center justify-between gap-3">
        {compact && (
          <motion.div
            layout
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50"
          >
            {icon}
          </motion.div>
        )}
        <div className="min-w-0">
          <p className={`${compact ? "text-[11px]" : "text-sm"} text-gray-500`}>{title}</p>
          <motion.div
            key={String(title) + String(value)}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className={`${compact ? "mt-0.5 text-base" : "mt-1 text-2xl"} font-bold`}
          >
            {value}
          </motion.div>
        </div>
        {!compact && (
          <motion.div layout className="rounded-lg bg-blue-50 p-3">
            {icon}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

interface SalesStatsProps {
  summary: Summary;
  selectedCountryCode?: string | null;
  orderCount: number;
  compact?: boolean;
}

const SalesStats = ({ summary, selectedCountryCode, orderCount, compact = false }: SalesStatsProps) => {
  const { language, country } = useAuth();
  const currencyBreakdown = (summary?.currency_breakdown || []).filter(
    (item) => Number(item.total_value_with_discount || 0) > 0
  );
  const relevantCurrencyBreakdown = selectedCountryCode
    ? currencyBreakdown.filter((item) => item.country_code === selectedCountryCode)
    : currencyBreakdown;

  const renderSalesValue = () => {
    if (selectedCountryCode && relevantCurrencyBreakdown.length === 0) {
      return <span>-</span>;
    }

    if (relevantCurrencyBreakdown.length <= 1) {
      const item = relevantCurrencyBreakdown[0];
      const symbol = getCurrencySymbol(item?.currency_code || item?.country_code || country);
      const value = Number(item?.total_value_with_discount ?? summary?.total_value_with_discount ?? 0);
      return <span>{formatCurrency(value, symbol)}</span>;
    }

    return (
      <div className="space-y-1">
        {relevantCurrencyBreakdown.map((item: CurrencySummary) => {
          const code = item.currency_code || item.country_code || "";
          const symbol = getCurrencySymbol(code || country);
          return (
            <div key={code} className="flex items-baseline gap-2">
              <span>{formatCurrency(Number(item.total_value_with_discount || 0), symbol)}</span>
              <span className="text-xs font-semibold text-gray-400">{code}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const stats = selectedCountryCode ? [
    {
      title: dictionary["total-sales"][language],
      value: renderSalesValue(),
      icon: <DollarSign className="text-fluxi" size={24} />,
    },
    {
      title: dictionary["products-sold"][language],
      value: <span>{summary?.total_units || 0}</span>,
      icon: <Package className="text-fluxi" size={24} />,
    },
    {
      title: dictionary["orders-tab"]?.[language] || "Órdenes",
      value: <span>{orderCount}</span>,
      icon: <Package className="text-fluxi" size={24} />,
    },
  ] : [
    {
      title: dictionary["products-sold"][language],
      value: <span>{summary?.total_units || 0}</span>,
      icon: <Package className="text-fluxi" size={24} />,
    },
    {
      title: dictionary["orders-tab"]?.[language] || "Órdenes",
      value: <span>{orderCount}</span>,
      icon: <Users className="text-fluxi" size={24} />,
    },
  ];

  return (
    <motion.div
      layout
      className={
        compact
          ? `flex flex-wrap justify-end gap-2`
          : `grid ${selectedCountryCode ? "grid-cols-3" : "grid-cols-2"} gap-4 mb-6`
      }
      transition={{ layout: { duration: 0.22, ease: "easeOut" } }}
    >
      {stats.map((s) => (
        <StatCard
          key={s.title}
          title={s.title}
          value={s.value}
          icon={s.icon}
          compact={compact}
        />
      ))}
    </motion.div>
  );
};

export default SalesStats;
